import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export const Hero = () => {
  return (
    <section className="relative flex items-start justify-center pt-[57px] overflow-hidden" aria-labelledby="hero-title">
      <div className="absolute inset-0 bg-background" aria-hidden="true" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-0">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <article className="text-left space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
              <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" aria-hidden="true" />
              <h1 id="hero-title" className="text-sm font-medium text-muted-foreground m-0">
                Online study room • Free forever • No limits
              </h1>
            </div>

            <p className="font-display italic text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" style={{ fontWeight: 400 }}>
              <span className="text-primary">Alone.</span>
              <br />
              <span className="text-foreground">But never lonely.</span>
            </p>

            <p className="text-base sm:text-lg text-accent font-light max-w-xl leading-relaxed">
              Study together. Focus together.
            </p>

            <p className="text-lg sm:text-xl text-muted-foreground font-light max-w-xl leading-relaxed">
              CSW is a free body doubling platform where you study alongside others on camera. Join live study streams, "study with me" sessions, and virtual rooms with people around the world — because you focus better when you're not alone.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 w-full">
              <Button 
                size="lg" 
                className="group bg-primary hover:bg-primary/90 text-primary-foreground font-sans font-light px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg glow-primary transition-all hover:scale-105 w-full sm:w-auto"
                asChild
              >
                <a href="/students/">
                  For students
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-border hover:bg-card font-sans font-light px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all hover:scale-105 w-full sm:w-auto"
                asChild
              >
                <a href="/institution/">
                  For institutions
                </a>
              </Button>
            </div>
            
            <aside className="flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-8 pt-8" aria-label="Key statistics">
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">10,000+</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Study Sessions</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">24/7</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Live Rooms</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">$0</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Always Free</p>
              </div>
            </aside>
          </article>
          
          <figure className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
              <video 
                src="/videos/productivity-demo.mp4" 
                autoPlay loop muted playsInline
                preload="metadata"
                className="w-full h-auto"
                aria-label="CSW study stream demo showing body doubling in action"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" aria-hidden="true" />
            </div>
            <figcaption className="sr-only">CSW body doubling platform — study together with live video accountability</figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
};
